import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import FormBackButton from './components/FormBackButton';
import PatientSupportFooter from './components/PatientSupportFooter';
import PageSeo from './seo/PageSeo';

/**
 * Página pública que explica a REGRA DA DATA em documentos emitidos por
 * teleconsulta assíncrona (Consulta+):
 *
 *   A DATA DE REFERÊNCIA de atestados, receitas e pedidos de exame é
 *   sempre a data de INÍCIO do chat da consulta — nunca depende do
 *   momento em que o médico responde ou assina o PDF. Isso decorre do
 *   próprio modelo assíncrono, reconhecido pela Res. CFM 2.314/2022 e
 *   pela Lei 14.510/2022, e das regras do CDC/CLT sobre validade de
 *   documentos com assinatura digital ICP-Brasil.
 *
 * Origem (03/08/2026): investigação de self-refunds mostrou que uma
 * fração relevante dos pedidos surge da ansiedade "e se a data do
 * atestado atrapalhar no meu trabalho?". Consolidar a resposta numa
 * página pública referenciável (chat, e-mails, Sofia, PDFs) transforma
 * a preocupação em confiança — sem esconder ou dificultar o reembolso.
 */

const COLORS = {
  bg: '#F9FAFB',
  surface: '#FFFFFF',
  primary: '#D32F2F',
  primaryDark: '#B71C1C',
  primarySoft: 'rgba(211,47,47,0.08)',
  primarySoftBorder: 'rgba(211,47,47,0.18)',
  text: '#1E1E1E',
  textSecondary: '#3A3A3A',
  textMuted: '#6B6B6B',
  border: 'rgba(0,0,0,0.08)',
  borderStrong: 'rgba(0,0,0,0.14)',
  shadowSm: '0 1px 2px rgba(0,0,0,0.04)',
  shadowMd: '0 6px 16px rgba(0,0,0,0.06)',
  shadowLg: '0 12px 28px rgba(0,0,0,0.08)',
  info: '#1E3A5F',
  infoBg: '#EFF6FF',
  infoBorder: '#BFDBFE',
  success: '#166534',
  successBg: '#F0FDF4',
  successBorder: '#BBF7D0',
};

const fontStack =
  "'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif";

const styles = {
  page: {
    minHeight: '100vh',
    background: COLORS.bg,
    fontFamily: fontStack,
    color: COLORS.text,
    WebkitFontSmoothing: 'antialiased',
  },
  header: {
    position: 'relative',
    background: COLORS.surface,
    borderBottom: `1px solid ${COLORS.border}`,
    padding: 'max(4.5rem, env(safe-area-inset-top, 0px)) 24px 56px',
    textAlign: 'center',
    overflow: 'hidden',
  },
  headerAccent: {
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(ellipse 60% 80% at 50% 0%, rgba(211,47,47,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  headerInner: {
    position: 'relative',
    maxWidth: 720,
    margin: '0 auto',
  },
  headerTitle: {
    fontSize: 'clamp(24px, 5.2vw, 36px)',
    fontWeight: 700,
    margin: '0 0 14px',
    lineHeight: 1.2,
    color: COLORS.text,
    letterSpacing: '-0.01em',
  },
  headerSub: {
    fontSize: 'clamp(15px, 2.6vw, 17px)',
    color: COLORS.textMuted,
    maxWidth: 600,
    margin: '0 auto',
    lineHeight: 1.6,
  },
  container: {
    maxWidth: 760,
    margin: '0 auto',
    padding: '32px 20px 64px',
  },
  keyClaim: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
    padding: '22px 24px',
    background: COLORS.successBg,
    border: `1px solid ${COLORS.successBorder}`,
    borderLeft: `4px solid ${COLORS.success}`,
    borderRadius: 14,
    marginBottom: 24,
    boxShadow: COLORS.shadowSm,
  },
  keyClaimBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 10,
    background: COLORS.success,
    color: '#FFFFFF',
    flexShrink: 0,
  },
  keyClaimTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#065F46',
    margin: '0 0 6px',
    lineHeight: 1.35,
  },
  keyClaimText: {
    fontSize: 14.5,
    lineHeight: 1.65,
    color: '#065F46',
    margin: 0,
  },
  infoCallout: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
    padding: '18px 22px',
    background: COLORS.infoBg,
    border: `1px solid ${COLORS.infoBorder}`,
    borderLeft: `4px solid #1D4ED8`,
    borderRadius: 14,
    marginBottom: 24,
    boxShadow: COLORS.shadowSm,
  },
  infoTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: COLORS.info,
    margin: '0 0 4px',
    lineHeight: 1.4,
  },
  infoText: {
    fontSize: 14,
    lineHeight: 1.65,
    color: COLORS.info,
    margin: 0,
  },
  section: {
    background: COLORS.surface,
    borderRadius: 16,
    padding: 'clamp(24px, 4vw, 32px)',
    marginBottom: 20,
    border: `1px solid ${COLORS.border}`,
    boxShadow: COLORS.shadowSm,
  },
  sectionTitle: {
    fontSize: 'clamp(18px, 3.4vw, 22px)',
    fontWeight: 700,
    margin: '0 0 6px',
    color: COLORS.text,
    letterSpacing: '-0.01em',
  },
  sectionEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1.2px',
    textTransform: 'uppercase',
    color: COLORS.primary,
    margin: '0 0 8px',
  },
  paragraph: {
    margin: '0 0 12px',
    fontSize: 15,
    lineHeight: 1.7,
    color: COLORS.textSecondary,
  },
  divider: {
    height: 1,
    background: COLORS.border,
    margin: '20px 0',
    border: 0,
  },
  bulletList: {
    margin: '0 0 12px',
    paddingLeft: 22,
    fontSize: 15,
    lineHeight: 1.7,
    color: COLORS.textSecondary,
  },
  legalList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  legalItem: {
    display: 'grid',
    gridTemplateColumns: '20px 1fr',
    gap: 12,
    alignItems: 'flex-start',
    fontSize: 14.5,
    lineHeight: 1.6,
    color: COLORS.textSecondary,
  },
  legalBullet: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    borderRadius: 6,
    background: COLORS.primary,
    color: '#fff',
    fontSize: 11,
    fontWeight: 800,
    marginTop: 2,
  },
  link: {
    color: COLORS.primary,
    fontWeight: 600,
    textDecoration: 'none',
    borderBottom: `1px solid ${COLORS.primarySoftBorder}`,
    transition: 'border-color 0.15s, color 0.15s',
  },
  faqList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  faqItem: {
    borderTop: `1px solid ${COLORS.border}`,
  },
  faqItemFirst: {
    borderTop: 'none',
  },
  faqButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '18px 4px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 600,
    color: COLORS.text,
    textAlign: 'left',
    fontFamily: 'inherit',
    transition: 'color 0.15s',
  },
  faqChevron: {
    flexShrink: 0,
    transition: 'transform 0.25s ease',
    color: COLORS.textMuted,
  },
  faqAnswer: {
    padding: '0 4px 18px',
    fontSize: 14.5,
    lineHeight: 1.7,
    color: COLORS.textSecondary,
    animation: 'faqOpen 0.2s ease-out',
  },
  helpBox: {
    background: COLORS.text,
    color: '#fff',
    borderRadius: 18,
    padding: 'clamp(24px, 4vw, 36px)',
    marginBottom: 24,
    boxShadow: COLORS.shadowLg,
    position: 'relative',
    overflow: 'hidden',
  },
  helpBoxAccent: {
    position: 'absolute',
    top: -60,
    right: -60,
    width: 200,
    height: 200,
    borderRadius: '50%',
    background:
      'radial-gradient(circle, rgba(211,47,47,0.45) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  helpInner: {
    position: 'relative',
  },
  helpEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '1.4px',
    textTransform: 'uppercase',
    color: '#FFB4B4',
    margin: '0 0 10px',
  },
  helpTitle: {
    fontSize: 'clamp(20px, 3.6vw, 24px)',
    fontWeight: 700,
    margin: '0 0 12px',
    color: '#fff',
    letterSpacing: '-0.01em',
  },
  helpText: {
    margin: '0 0 20px',
    fontSize: 15,
    lineHeight: 1.65,
    color: 'rgba(255,255,255,0.78)',
  },
  helpCtaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
  },
  helpCta: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 18px',
    borderRadius: 10,
    background: COLORS.primary,
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    textDecoration: 'none',
    transition: 'background 0.15s, transform 0.08s',
  },
  helpCtaSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 18px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    textDecoration: 'none',
    border: '1px solid rgba(255,255,255,0.2)',
    transition: 'background 0.15s, transform 0.08s',
  },
  footer: {
    textAlign: 'center',
    padding: '24px 20px 48px',
    color: COLORS.textMuted,
    fontSize: 13,
    lineHeight: 1.6,
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    margin: '4px 0 0',
  },
  timelineStep: {
    display: 'grid',
    gridTemplateColumns: '34px 1fr',
    gap: 14,
    alignItems: 'flex-start',
  },
  timelineDot: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    borderRadius: '50%',
    fontSize: 15,
    flexShrink: 0,
    background: COLORS.primarySoft,
    border: `1px solid ${COLORS.primarySoftBorder}`,
  },
  timelineConnector: {
    width: 2,
    height: 26,
    background: COLORS.borderStrong,
    margin: '4px 0 4px 16px',
    borderRadius: 2,
  },
  timelineWhen: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: COLORS.textMuted,
    margin: '0 0 2px',
  },
  timelineWhat: {
    fontSize: 14.5,
    lineHeight: 1.6,
    color: COLORS.textSecondary,
    margin: 0,
    paddingTop: 0,
  },
  timelineResult: {
    marginTop: 18,
    padding: '14px 18px',
    background: COLORS.successBg,
    border: `1px solid ${COLORS.successBorder}`,
    borderRadius: 12,
    fontSize: 14.5,
    lineHeight: 1.65,
    color: '#065F46',
  },
  shareButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 18px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.08)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    border: '1px solid rgba(255,255,255,0.2)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 0.15s, transform 0.08s',
  },
};

function ChevronIcon({ open }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        ...styles.faqChevron,
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckShieldIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

const PAGE_URL = 'https://consultamais.app/data-atestado';

/**
 * Botão "Compartilhar" pensado para o paciente encaminhar a página ao RH:
 * usa o share sheet nativo no celular e cai para copiar o link no desktop.
 */
function ShareButton() {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    const shareData = {
      title: 'A data do atestado e da receita na teleconsulta | Consulta+',
      text:
        'Base legal da data em documentos emitidos por teleconsulta: a referência é sempre o início do atendimento.',
      url: PAGE_URL,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(PAGE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      window.prompt('Copie o link da página:', PAGE_URL);
    }
  }, []);

  return (
    <button
      type="button"
      style={styles.shareButton}
      className="data-atestado-cta-secondary"
      onClick={handleShare}
    >
      {copied ? '✓ Link copiado!' : 'Compartilhar esta página'}
    </button>
  );
}

const TIMELINE_STEPS = [
  {
    icon: '💬',
    when: 'Terça, 22h47',
    what: (
      <>
        Você conclui o pagamento e o <strong>chat com o médico é aberto</strong>.
        Para a lei, o atendimento <strong>começou aqui</strong> — mesmo que
        você vá dormir e continue a conversa no dia seguinte.
      </>
    ),
  },
  {
    icon: '🩺',
    when: 'Quarta, 07h30',
    what: (
      <>
        O médico assume o caso, conversa com você, define a conduta e{' '}
        <strong>assina digitalmente</strong> o atestado.
      </>
    ),
  },
  {
    icon: '📄',
    when: 'Documento emitido',
    what: (
      <>
        O PDF chega ao seu e-mail com a data de referência de{' '}
        <strong>terça, 22h47</strong> — o momento em que o chat foi aberto,
        não o momento da assinatura.
      </>
    ),
  },
];

function FAQ({ question, children, isFirst }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  return (
    <div style={{ ...styles.faqItem, ...(isFirst ? styles.faqItemFirst : {}) }}>
      <button
        type="button"
        style={{
          ...styles.faqButton,
          color: hover || open ? COLORS.primary : COLORS.text,
        }}
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-expanded={open}
      >
        <span>{question}</span>
        <ChevronIcon open={open} />
      </button>
      {open && <div style={styles.faqAnswer}>{children}</div>}
    </div>
  );
}

const DataAtestado = () => {
  const navigate = useNavigate();
  const handleBack = () => navigate('/');

  return (
    <>
      <FormBackButton onClick={handleBack} ariaLabel="Voltar para a home" />
      <div style={styles.page}>
    <PageSeo
      path="/data-atestado"
      title="A data do atestado e da receita — teleconsulta por chat | Consulta+"
      description="Como funciona a data em documentos emitidos por teleconsulta assíncrona: a referência é sempre a data de início do chat. Base legal (Res. CFM 2.314/2022, Lei 14.510/2022) e o que fazer se a empresa questionar."
      breadcrumbs={[
        { name: 'Início', url: '/' },
        { name: 'Data do atestado ou receita', url: '/data-atestado' },
      ]}
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Qual é a data que vale no meu atestado ou receita emitido por teleconsulta?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'A data de referência é sempre a data de início do chat da consulta na Consulta+, e não a data em que o médico responde ou assina o PDF. Esse é o funcionamento normal da teleconsulta assíncrona reconhecida pela Res. CFM 2.314/2022 e pela Lei 14.510/2022.',
            },
          },
          {
            '@type': 'Question',
            name: 'A empresa ou o órgão público pode recusar por causa da data?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Não. Documentos médicos emitidos por teleconsulta com assinatura digital ICP-Brasil têm a mesma validade jurídica dos presenciais (MP 2.200-2/2001, Lei 14.063/2020, Lei 14.510/2022, Res. CFM 2.314/2022). Empresas e órgãos públicos são obrigados a aceitá-los.',
            },
          },
          {
            '@type': 'Question',
            name: 'O médico assinou depois da data de início do chat. Isso invalida?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Não. A modalidade assíncrona é regulamentada e é normal que a assinatura ocorra em momento posterior ao início do atendimento. O que vale para efeitos legais (afastamento, prescrição, pedido de exame) é a data de início do chat.',
            },
          },
          {
            '@type': 'Question',
            name: 'O atestado tem data de emissão no cabeçalho e a expressão "a partir de data X" no corpo. Isso vale?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sim. Em atestado digital com assinatura ICP-Brasil a data do cabeçalho/emissão é fixa por determinação legal e técnica. Quando o afastamento precisa valer a partir de data anterior à emissão do PDF, o procedimento correto é inserir no corpo a observação "a partir de data X". Essa observação basta para validar o período. Há registro formal do início da consulta que justifica o documento nesses termos, e a empresa ou órgão público não pode recusar só por causa dessa diferença de datas.',
            },
          },
        ],
      }}
    />
    <style>{`
      @keyframes faqOpen {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
      }
      a.data-atestado-link:hover {
        border-bottom-color: ${COLORS.primary} !important;
      }
      a.data-atestado-cta:hover {
        background: ${COLORS.primaryDark} !important;
        transform: translateY(-1px);
      }
      a.data-atestado-cta-secondary:hover {
        background: rgba(255,255,255,0.14) !important;
      }
      a.data-atestado-footer-link:hover {
        color: ${COLORS.primary} !important;
        border-bottom-color: ${COLORS.primary} !important;
      }
    `}</style>

    <header style={styles.header}>
      <div style={styles.headerAccent} aria-hidden="true" />
      <div style={styles.headerInner}>
        <h1 style={styles.headerTitle}>
          A data do seu atestado ou receita
        </h1>
        <p style={styles.headerSub}>
          Como funciona a data em documentos emitidos por teleconsulta
          assíncrona na Consulta+, base legal e o que fazer se a sua empresa
          ou órgão público questionar.
        </p>
        <img
          src="/img/heros/sec-home-trabalhador-16x9.webp"
          alt="Pintura modernista de um paciente descansando sob uma árvore ao lado da bicicleta enquanto fala pelo celular com a médica"
          loading="lazy"
          style={{
            width: '100%',
            maxWidth: 640,
            aspectRatio: '16 / 8',
            objectFit: 'cover',
            borderRadius: 16,
            boxShadow: '0 6px 18px rgba(15,23,42,0.12)',
            display: 'block',
            margin: '22px auto 0',
          }}
        />
      </div>
    </header>

    <div style={styles.container}>
      <div style={styles.keyClaim} role="note">
        <span style={styles.keyClaimBadge} aria-hidden="true">
          <CheckShieldIcon />
        </span>
        <div>
          <p style={styles.keyClaimTitle}>
            A data que vale é sempre a de <em>início do chat</em>, não a de
            emissão do PDF.
          </p>
          <p style={styles.keyClaimText}>
            Enquanto você aguarda o médico responder, o seu atendimento{' '}
            <strong>já começou</strong> para efeitos legais. Atestado,
            receita e pedido de exame consideram a{' '}
            <strong>data e a hora de início do chat</strong> — independentemente
            do momento em que o profissional assina o documento. Esperar
            algumas horas ou até o dia seguinte <strong>não altera nada</strong>{' '}
            no seu documento.
          </p>
        </div>
      </div>

      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>Exemplo prático</p>
        <h2 style={styles.sectionTitle}>
          Chat aberto à noite, resposta de manhã — que data vale?
        </h2>
        <hr style={styles.divider} />
        <div style={styles.timeline}>
          {TIMELINE_STEPS.map((step, i) => (
            <React.Fragment key={step.when}>
              {i > 0 && <div style={styles.timelineConnector} aria-hidden="true" />}
              <div style={styles.timelineStep}>
                <span style={styles.timelineDot} aria-hidden="true">
                  {step.icon}
                </span>
                <div>
                  <p style={styles.timelineWhen}>{step.when}</p>
                  <p style={styles.timelineWhat}>{step.what}</p>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <div style={styles.timelineResult}>
          <strong>Resultado:</strong> se o afastamento indicado for de 1 dia, ele
          cobre a terça — o dia em que você procurou o atendimento. É a mesma
          lógica de um pronto-socorro: quem dá entrada às 23h é registrado com
          atendimento daquele dia, ainda que o médico examine horas depois.
        </div>
      </section>

      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>O que é a teleconsulta assíncrona</p>
        <h2 style={styles.sectionTitle}>
          Chat em vez de vídeo — modalidade reconhecida por lei
        </h2>
        <hr style={styles.divider} />
        <p style={styles.paragraph}>
          A Consulta+ é uma <strong>teleconsulta por chat</strong>: você
          descreve a sua queixa por texto, envia fotos ou exames quando
          precisar, e o médico com CRM ativo (Dr. Guilherme Esmanhotto —
          CRM/DF 23.706) lê o seu caso e responde <strong>quando estiver
          disponível</strong>. Os tempos médios de início estão nos nossos
          Termos de Uso; enquanto a consulta não iniciar, o paciente pode
          solicitar reembolso integral.
        </p>
        <p style={styles.paragraph}>
          Isso é a modalidade <strong>assíncrona</strong> da telemedicina —
          diferente do vídeo em tempo real (síncrona). Nas duas modalidades,
          o atendimento tem a mesma validade legal. Diferentemente do vídeo,
          o chat permite que <strong>paciente e médico respondam quando cada
          um puder</strong>, o que é uma vantagem para quem precisa consultar
          fora do horário comercial, no trabalho ou cuidando de alguém.
        </p>
        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
          Para todos os efeitos administrativos, jurídicos e trabalhistas, a
          consulta é considerada realizada a partir do <strong>momento em
          que o chat é aberto</strong> — e é essa data e hora que aparecem
          nos documentos emitidos.
        </p>
      </section>

      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>Regra clara</p>
        <h2 style={styles.sectionTitle}>
          O que a data no documento realmente significa
        </h2>
        <hr style={styles.divider} />
        <ul style={styles.bulletList}>
          <li>
            <strong>Atestado de afastamento:</strong> os dias contam a partir
            da <strong>data e hora de início do chat</strong>. Se você abriu
            o chat na terça e o médico assinou o PDF na quarta, o atestado
            vale a partir da terça.
          </li>
          <li>
            <strong>Receita:</strong> a validade do medicamento (30, 60 ou 90
            dias, conforme o tipo) conta a partir da{' '}
            <strong>data de início do chat</strong>. É essa data que aparece
            no documento.
          </li>
          <li>
            <strong>Pedido de exame:</strong> mesma regra. O laboratório
            considera a data do início do chat como a data do pedido médico.
          </li>
          <li>
            <strong>Data de emissão / assinatura digital:</strong> aparece
            separadamente no PDF (e no relatório do validador do ITI) apenas
            para fins de rastreabilidade. Ela <strong>não substitui</strong>{' '}
            a data de referência do documento.
          </li>
        </ul>
        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
          É por isso que <strong>não faz diferença</strong> se o médico
          responde no mesmo minuto, em uma hora ou depois de algumas horas:
          o seu documento tem a data que você começou a consulta.
        </p>
      </section>

      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>Observação no corpo do atestado</p>
        <h2 style={styles.sectionTitle}>
          Por que a data de emissão é fixa — e por que &quot;a partir de
          data&nbsp;X&quot; no texto basta
        </h2>
        <hr style={styles.divider} />
        <p style={styles.paragraph}>
          Em atestado médico digital com assinatura ICP-Brasil, a{' '}
          <strong>data do cabeçalho / data de emissão</strong> é{' '}
          <strong>fixa por determinação legal e técnica</strong>: o sistema
          de emissão e o carimbo de tempo da assinatura registram o momento
          em que o PDF é gerado e assinado. O médico{' '}
          <strong>não pode alterar à mão</strong> essa data do cabeçalho
          para &quot;voltar&quot; o documento no tempo — isso é uma regra de
          integridade do documento digital, não uma falha nem um artifício.
        </p>
        <p style={styles.paragraph}>
          Quando o afastamento precisa valer a partir de uma data{' '}
          <strong>anterior à emissão do PDF</strong> (por exemplo, o chat
          começou ontem e a assinatura ocorreu hoje), o procedimento correto
          e rotineiro é inserir no <strong>corpo do texto</strong> do
          atestado a expressão{' '}
          <strong>&quot;a partir de [data X]&quot;</strong> (ou equivalente).
          Essa observação no corpo{' '}
          <strong>basta para validar o afastamento a partir da data
          indicada</strong>: o cabeçalho continua com a data de emissão
          (fixa), e o texto clínico define o período de vigência do
          afastamento.
        </p>
        <p style={styles.paragraph}>
          Isso é <strong>completamente regular</strong>. Fazemos assim de
          forma habitual na teleconsulta assíncrona justamente porque existe{' '}
          <strong>registro formal, datado e horado, do início da
          consulta</strong> (abertura do chat após o pagamento, com
          rastreabilidade na plataforma). Esse marco temporal justifica
          documentar o afastamento a partir daquela data — mesmo quando a
          assinatura do PDF ocorre horas ou um dia depois. Não se trata de
          inventar um período sem atendimento: há prova objetiva de quando a
          consulta começou.
        </p>
        <p style={styles.paragraph}>
          Nestes termos, <strong>a empresa ou o órgão público não pode
          recusar</strong> o atestado só porque a data do cabeçalho/emissão
          difere da data indicada na observação do corpo. O documento
          permanece idôneo: médico com CRM ativo, assinatura digital
          ICP-Brasil, registro formal do início do atendimento e texto
          expresso do período de afastamento. A recusa baseada apenas nessa
          diferença de datas contraria a Lei&nbsp;14.510/2022, a
          Res.&nbsp;CFM&nbsp;2.314/2022 e as regras de validade da
          assinatura digital (MP&nbsp;2.200-2/2001 e Lei&nbsp;14.063/2020).
        </p>
        <p style={{ ...styles.paragraph, marginBottom: 0 }}>
          Em resumo: cabeçalho fixo (emissão) + observação no corpo
          (&quot;a partir de data&nbsp;X&quot;) + registro formal do início
          da consulta = procedimento padrão, válido e aceitável perante RH,
          empresa e órgão público.
        </p>
      </section>

      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>Base legal</p>
        <h2 style={styles.sectionTitle}>
          Por que empresa ou órgão público não pode recusar
        </h2>
        <hr style={styles.divider} />
        <p style={styles.paragraph}>
          Documentos médicos emitidos por teleconsulta na Consulta+ têm{' '}
          <strong>a mesma validade jurídica</strong> dos emitidos em
          atendimento presencial, com fundamento em:
        </p>
        <ol style={styles.legalList}>
          <li style={styles.legalItem}>
            <span style={styles.legalBullet}>1</span>
            <span>
              <strong>Lei nº 14.510/2022</strong> — reconheceu definitivamente
              a telemedicina no Brasil e equiparou seus documentos aos do
              atendimento presencial para todos os fins (art. 6º-A).
            </span>
          </li>
          <li style={styles.legalItem}>
            <span style={styles.legalBullet}>2</span>
            <span>
              <strong>Resolução CFM nº 2.314/2022</strong> — regulamenta a
              telemedicina, autoriza expressamente a modalidade{' '}
              <strong>assíncrona</strong> (art. 3º, IV) e a emissão de
              receitas, atestados e pedidos de exame por essa via (art. 4º,
              §§ 1º e 2º).
            </span>
          </li>
          <li style={styles.legalItem}>
            <span style={styles.legalBullet}>3</span>
            <span>
              <strong>Res. CFM 2.314/2022, art. 6º, § 5º</strong> — veda
              atestado <em>retroativo</em> (cobrir período anterior ao
              atendimento). A regra <strong>reforça</strong>, e não contraria,
              que a data válida é a de <strong>início da consulta</strong>{' '}
              — que na modalidade assíncrona é a abertura do chat.
            </span>
          </li>
          <li style={styles.legalItem}>
            <span style={styles.legalBullet}>4</span>
            <span>
              <strong>MP nº 2.200-2/2001</strong> e <strong>Lei nº
              14.063/2020</strong> — todo documento assinado com certificado
              digital <strong>ICP-Brasil</strong> tem validade jurídica plena,
              equivalente à assinatura física em papel. Empresas, órgãos
              públicos, farmácias e laboratórios são obrigados a aceitar.
            </span>
          </li>
          <li style={styles.legalItem}>
            <span style={styles.legalBullet}>5</span>
            <span>
              <strong>CLT, art. 6º, § 2º da Lei nº 605/49 e Súmula 15
              TST</strong> — o empregador é obrigado a aceitar atestado
              médico idôneo para justificar a falta ao trabalho. A validade
              do documento independe da modalidade em que a consulta foi
              realizada, desde que assinado por médico com CRM ativo — o que
              é o caso da Consulta+.
            </span>
          </li>
        </ol>
      </section>

      <section style={styles.section}>
        <p style={styles.sectionEyebrow}>Dúvidas comuns</p>
        <h2 style={styles.sectionTitle}>Perguntas frequentes</h2>
        <hr style={styles.divider} />

        <div style={styles.faqList}>
          <FAQ
            question="A data no meu atestado é a data em que eu abri o chat, não a data em que o médico assinou. Isso está certo?"
            isFirst
          >
            <p style={{ margin: '0 0 8px' }}>
              Sim, está correto. Na teleconsulta por chat da Consulta+ a data
              de referência do documento é sempre a{' '}
              <strong>data e a hora de início do chat</strong>. É essa data
              que aparece no atestado, na receita e no pedido de exame.
            </p>
            <p style={{ margin: 0 }}>
              A data e a hora em que o médico <em>assinou digitalmente</em> o
              PDF ficam registradas separadamente (como carimbo de tempo da
              assinatura ICP-Brasil), apenas para rastreabilidade — não
              substituem a data do documento.
            </p>
          </FAQ>

          <FAQ question="Minha empresa disse que só aceita atestado do dia em que o médico assinou. Estou coberto(a)?">
            <p style={{ margin: '0 0 8px' }}>
              Sim. Pela <strong>Lei 14.510/2022</strong> e pela{' '}
              <strong>Res. CFM 2.314/2022</strong>, a data válida do documento
              é a data em que <strong>o atendimento começou</strong> — na
              modalidade assíncrona por chat, isso equivale à abertura da
              conversa com o médico.
            </p>
            <p style={{ margin: 0 }}>
              Se o RH tiver dúvida, você pode compartilhar esta página e o
              tutorial de autenticação em{' '}
              <a
                href="/autenticar"
                style={styles.link}
                className="data-atestado-link"
              >
                consultamais.app/autenticar
              </a>
              , onde a empresa pode confirmar no portal oficial do Governo
              Federal (ITI) que o documento é autêntico e íntegro.
            </p>
          </FAQ>

          <FAQ question="O médico só respondeu horas depois. Isso significa que a consulta foi feita fora do prazo do atestado?">
            <p style={{ margin: 0 }}>
              Não. Enquanto o chat estava aberto e você aguardava, o
              atendimento <strong>já estava em curso</strong> — essa é a
              característica da modalidade assíncrona. Os dias de afastamento
              ou a validade da receita começam a contar a partir do{' '}
              <strong>início do chat</strong>, não do momento em que o médico
              digitou a resposta.
            </p>
          </FAQ>

          <FAQ question="E se o chat começou às 23h e o médico respondeu no dia seguinte às 8h?">
            <p style={{ margin: 0 }}>
              O documento tem a data <strong>do início do chat</strong> (o
              dia das 23h). É a mesma lógica de um plantão hospitalar: o
              paciente que dá entrada às 23h é registrado com atendimento
              iniciado naquele dia, ainda que o médico o examine algumas
              horas depois.
            </p>
          </FAQ>

          <FAQ question='O médico escreveu "a partir de data X" no corpo, mas o cabeçalho tem outra data. A empresa pode recusar?'>
            <p style={{ margin: '0 0 8px' }}>
              <strong>Não.</strong> Em atestado digital, a data do
              cabeçalho/emissão é <strong>fixa</strong> (não pode ser
              alterada no sistema). O procedimento correto e regular é
              colocar no <strong>corpo do texto</strong> a expressão
              &quot;a partir de data&nbsp;X&quot; — essa observação{' '}
              <strong>basta</strong> para o afastamento valer a partir da
              data indicada.
            </p>
            <p style={{ margin: '0 0 8px' }}>
              Isso é justificado pelo <strong>registro formal do início da
              consulta</strong> (abertura do chat, com data e hora
              rastreadas na plataforma). Documentar o afastamento a partir
              dessa data, mesmo com assinatura do PDF em momento posterior,
              é prática habitual e plenamente regular na teleconsulta
              assíncrona.
            </p>
            <p style={{ margin: 0 }}>
              A empresa ou o órgão público <strong>não pode recusar</strong>{' '}
              o documento só porque a data do cabeçalho difere da data
              indicada na observação do corpo. Encaminhe esta página ao RH
              se houver dúvida.
            </p>
          </FAQ>

          <FAQ question="Posso pedir atestado com data retroativa (para um dia em que eu fiquei em casa antes de abrir o chat)?">
            <p style={{ margin: '0 0 8px' }}>
              <strong>Não</strong> para cobrir um dia{' '}
              <em>anterior à abertura do chat</em>. A Res. CFM 2.314/2022,
              art. 6º, § 5º, veda atestado com data anterior à do
              atendimento — inclusive na teleconsulta. Se você precisa
              cobrir um período anterior ao início da consulta, converse
              diretamente com o RH da sua empresa: essa é uma decisão
              administrativa, não médica.
            </p>
            <p style={{ margin: 0 }}>
              Já o caso em que o médico coloca &quot;a partir de data&nbsp;X&quot;
              no corpo porque a <strong>emissão do PDF</strong> ocorreu
              depois do <strong>início formal da consulta</strong> é outra
              situação — e é regular, conforme explicado acima.
            </p>
          </FAQ>

          <FAQ question="A receita começa a valer a partir de qual data?">
            <p style={{ margin: 0 }}>
              Da <strong>data de início do chat</strong>, que aparece no PDF.
              A validade legal do tipo de receita (por exemplo, 30 dias para
              controle especial branco) é contada dessa data em diante — a
              farmácia observa o mesmo prazo do papel.
            </p>
          </FAQ>

          <FAQ question="Como comprovar autenticidade se a empresa questionar?">
            <p style={{ margin: '0 0 8px' }}>
              Todo documento da Consulta+ é assinado com certificado{' '}
              <strong>ICP-Brasil</strong> — a mesma infraestrutura oficial
              usada pelo governo federal. A empresa pode conferir a
              autenticidade em segundos no portal oficial do ITI (validar.iti.gov.br).
              O passo a passo está em{' '}
              <a
                href="/autenticar"
                style={styles.link}
                className="data-atestado-link"
              >
                consultamais.app/autenticar
              </a>
              .
            </p>
            {/* Easycheck temporariamente fora do ar (18/08/2026). Religar: descomentar.
            <p style={{ margin: 0 }}>
              Empresas e departamentos de RH que preferirem um relatório
              formal também podem usar o{' '}
              <a
                href="/easycheck"
                style={styles.link}
                className="data-atestado-link"
              >
                Easycheck
              </a>
              {' '}(R$ 3,99 por documento).
            </p>
            */}
          </FAQ>

          <FAQ question="Se a empresa recusar mesmo assim, o que faço?">
            <p style={{ margin: 0 }}>
              Pela legislação brasileira, a empresa <strong>não pode</strong>{' '}
              recusar um atestado idôneo, assinado por médico com CRM ativo e
              com assinatura digital ICP-Brasil. Se ainda houver dúvida, o
              melhor caminho é: (1) mostrar esta página ao RH; (2) rodar a
              validação em validar.iti.gov.br; (3) em último caso, procurar o
              sindicato da categoria ou o Ministério do Trabalho.
            </p>
          </FAQ>

          <FAQ question="Preciso cancelar a consulta porque o médico está demorando demais para não perder o dia no atestado?">
            <p style={{ margin: '0 0 8px' }}>
              <strong>Não é necessário.</strong> Aguardar não altera nada nos
              seus documentos — a data neles vai ser a do início do chat, não
              a de quando o médico respondeu.
            </p>
            <p style={{ margin: 0 }}>
              Ainda assim, a garantia continua: enquanto o atendimento
              efetivo não começou (o médico ainda não enviou conduta, receita
              ou atestado), o <strong>reembolso integral</strong> está
              garantido — solicitado diretamente no chat pelo card &quot;Mudou
              de ideia?&quot; ou pelo suporte. É seu direito. Só quisemos
              deixar claro que a preocupação com a data <em>em si</em> não
              justifica o cancelamento.
            </p>
          </FAQ>
        </div>
      </section>

      <section style={styles.helpBox} aria-label="Ajuda adicional">
        <div style={styles.helpBoxAccent} aria-hidden="true" />
        <div style={styles.helpInner}>
          <p style={styles.helpEyebrow}>Ainda com dúvidas?</p>
          <p style={styles.helpTitle}>
            Encaminhe esta página ou use as ferramentas abaixo
          </p>
          <p style={styles.helpText}>
            Se o seu RH tiver questionamentos, encaminhe esta página e o
            tutorial de autenticação no ITI.
          </p>
          <div style={styles.helpCtaRow}>
            <a
              href="/autenticar"
              style={styles.helpCta}
              className="data-atestado-cta"
            >
              Como autenticar no ITI →
            </a>
            {/* Easycheck temporariamente fora do ar (18/08/2026). Religar: descomentar.
            <a
              href="/easycheck"
              style={styles.helpCtaSecondary}
              className="data-atestado-cta-secondary"
            >
              Easycheck para RH →
            </a>
            */}
            <ShareButton />
          </div>
        </div>
      </section>

      <PatientSupportFooter />

      <footer style={styles.footer}>
        Consulta+ · Dr. Guilherme Esmanhotto (CRM/DF 23.706) ·{' '}
        <a
          href="/termos-de-uso"
          style={{
            color: COLORS.text,
            fontWeight: 600,
            textDecoration: 'none',
            borderBottom: `1px solid ${COLORS.borderStrong}`,
          }}
          className="data-atestado-footer-link"
        >
          Termos de Uso
        </a>
      </footer>
      </div>
    </div>
    </>
  );
};

export default DataAtestado;
